<p-toast></p-toast>
<p-progressBar mode="indeterminate" class="m-2" *ngIf="isLoading"></p-progressBar>

<app-page-shell>
    <app-page-hero eyebrow="Reports" title="Sales Register" subtitle="Review posted sales vouchers, line costs, gross profit, and GP percentage.">
        <div actions class="report-actions">
            <p-button label="Generate" icon="pi pi-refresh" (onClick)="applyFilters()"></p-button>
            <p-button label="Export" icon="pi pi-file-excel" severity="success" (onClick)="exportExcel()"></p-button>
            <p-button label="Download PDF" icon="pi pi-file-pdf" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('download')" (onClick)="printReport(true)"></p-button>
            <p-button label="Print" icon="pi pi-print" severity="secondary" [outlined]="true" [loading]="reportActions.isPending('print')" (onClick)="printReport()"></p-button>
            <app-report-email reportName="Sales Register" [rows]="rows"></app-report-email>
        </div>
    </app-page-hero>

    <app-page-form-section icon="pi pi-filter" title="Report Filters" subtitle="Choose voucher date, customer, and reference filters.">
        <div class="report-filter-grid">
            <p-iftalabel>
                <p-datePicker inputId="salesRegisterStartDate" [(ngModel)]="startDate" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="salesRegisterStartDate">Start Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-datePicker inputId="salesRegisterEndDate" [(ngModel)]="endDate" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="salesRegisterEndDate">End Date</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-multiSelect inputId="salesRegisterCustomers" [options]="customers" [(ngModel)]="selectedCustomers" (onChange)="applyFilters()" optionLabel="entityName" display="chip" [filter]="true" [showClear]="true" styleClass="w-full"></p-multiSelect>
                <label for="salesRegisterCustomers">Customer</label>
            </p-iftalabel>
            <p-iftalabel>
                <input id="salesRegisterInvoiceNumber" pInputText [(ngModel)]="invoiceNumber" class="w-full" />
                <label for="salesRegisterInvoiceNumber">Invoice Number</label>
            </p-iftalabel>
            <p-iftalabel>
                <input id="salesRegisterShipmentNumber" pInputText [(ngModel)]="shipmentNumber" class="w-full" />
                <label for="salesRegisterShipmentNumber">Shipment Number</label>
            </p-iftalabel>
            <p-iftalabel>
                <input id="salesRegisterDeliveryNumber" pInputText [(ngModel)]="deliveryNumber" class="w-full" />
                <label for="salesRegisterDeliveryNumber">Delivery Number</label>
            </p-iftalabel>
            <p-iftalabel>
                <app-logistics-project-lookup inputId="salesRegisterLogisticsProject" [(ngModel)]="selectedLogisticsProjectId"></app-logistics-project-lookup>
                <label for="salesRegisterLogisticsProject">Logistics Project</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-multiSelect inputId="salesRegisterInvoiceTypes" [options]="invoiceTypeOptions" [(ngModel)]="selectedInvoiceTypes" display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                <label for="salesRegisterInvoiceTypes">Invoice Type</label>
            </p-iftalabel>
            <p-iftalabel>
                <p-multiSelect inputId="salesRegisterZatcaStatuses" [options]="zatcaStatusOptions" [(ngModel)]="selectedZatcaStatuses" display="chip" [showClear]="true" styleClass="w-full"></p-multiSelect>
                <label for="salesRegisterZatcaStatuses">ZATCA Status</label>
            </p-iftalabel>
            <div class="filter-buttons">
                <p-button label="Apply" icon="pi pi-check" (onClick)="applyFilters()"></p-button>
                <p-button label="Reset" icon="pi pi-filter-slash" severity="secondary" [outlined]="true" (onClick)="clearFilters()"></p-button>
            </div>
        </div>
    </app-page-form-section>

    <app-page-data-panel *ngIf="diagnostics.length" title="Report Diagnostics" [resultText]="diagnostics.length + ' checks'">
        <p-table [value]="diagnostics" styleClass="p-datatable-gridlines dense-table" [tableStyle]="{ 'min-width': '62rem' }">
            <ng-template pTemplate="header">
                <tr>
                    <th>Severity</th>
                    <th>Code</th>
                    <th>Message</th>
                    <th>Reference</th>
                    <th class="text-right">Amount</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-diagnostic>
                <tr>
                    <td>{{ diagnostic.severity }}</td>
                    <td>{{ diagnostic.code }}</td>
                    <td>{{ diagnostic.message }}</td>
                    <td>{{ diagnostic.referenceNumber || diagnostic.referenceId || '-' }}</td>
                    <td class="text-right">{{ diagnostic.amount ?? '-' }}</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>

    <div class="summary-grid">
        <p-card header="Gross Total" styleClass="summary-card">
            <div class="summary-amount">{{ summary.grossTotal | currency: currencyCode() : 'symbol' : '1.2-2' }}</div>
        </p-card>
        <p-card header="Sales" styleClass="summary-card">
            <div class="summary-amount">{{ summary.salesAmount | currency: currencyCode() : 'symbol' : '1.2-2' }}</div>
        </p-card>
        <p-card header="VAT" styleClass="summary-card">
            <div class="summary-amount">{{ summary.vatAmount | currency: currencyCode() : 'symbol' : '1.2-2' }}</div>
        </p-card>
        <p-card header="Cost of Sales" styleClass="summary-card">
            <div class="summary-amount">{{ summary.costOfSales | currency: currencyCode() : 'symbol' : '1.2-2' }}</div>
        </p-card>
        <p-card header="Gross Profit" styleClass="summary-card">
            <div class="summary-amount" [ngClass]="{'text-green-600': summary.grossProfit >= 0, 'text-red-600': summary.grossProfit < 0}">
                {{ summary.grossProfit | currency: currencyCode() : 'symbol' : '1.2-2' }}
            </div>
        </p-card>
        <p-card header="GP %" styleClass="summary-card">
            <div class="summary-amount">{{ summary.grossProfitPercent | number:'1.2-2' }}%</div>
        </p-card>
    </div>

    <app-page-data-panel title="Voucher Register" [resultText]="(totalRecords || 0) + ' vouchers'">
        <p-table
            [value]="rows"
            dataKey="invoiceId"
            [lazy]="true"
            [paginator]="true"
            [rows]="rowsPerPage"
            [first]="first"
            [totalRecords]="totalRecords"
            [rowsPerPageOptions]="[25, 50, 100]"
            [resizableColumns]="true"
            [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} vouchers"
            styleClass="p-datatable-gridlines dense-table"
            [tableStyle]="{ 'min-width': '118rem' }"
            (onLazyLoad)="loadSalesRegister($event)">
            <ng-template pTemplate="header">
                <tr>
                    <th style="width: 3rem"></th>
                    <th>Date</th>
                    <th>Particulars</th>
                    <th>Vch No</th>
                    <th class="text-right">Value / Gross Total</th>
                    <th class="text-right">Sales</th>
                    <th class="text-right">VAT</th>
                    <th class="text-right">Cost of Sales</th>
                    <th class="text-right">Item Profit</th>
                    <th class="text-right">Gross Profit</th>
                    <th class="text-right">GP %</th>
                    <th>Type</th>
                    <th>Trace</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row let-expanded="expanded">
                <tr class="voucher-row">
                    <td>
                        <button type="button" pButton [pRowToggler]="row" class="p-button-text p-button-rounded p-button-plain" [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></button>
                    </td>
                    <td>{{ row.invoiceDate | date:'dd-MMM-yy' }}</td>
                    <td>
                        <div class="particulars">{{ row.customerName || '-' }}</div>
                        <div class="muted" *ngIf="row.shipmentNumber">{{ row.shipmentNumber }}</div>
                    </td>
                    <td>
                        <button pButton type="button" class="p-button-link compact-link" [label]="row.invoiceNumber" (click)="openInvoice(row)"></button>
                    </td>
                    <td class="text-right">{{ row.grossTotal | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ row.salesAmount | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ row.vatAmount | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ row.costOfSales | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ row.itemProfit | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right" [ngClass]="{'text-green-600': row.grossProfit >= 0, 'text-red-600': row.grossProfit < 0}">
                        {{ row.grossProfit | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}
                    </td>
                    <td class="text-right">{{ row.grossProfitPercent | number:'1.2-2' }}%</td>
                    <td>{{ row.invoiceType || '-' }}</td>
                    <td>
                        <button pButton type="button" icon="pi pi-external-link" class="p-button-text p-button-sm" (click)="openInvoice(row)"></button>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="expandedrow" let-row>
                <tr>
                    <td colspan="13" class="line-detail-cell">
                        <p-table [value]="row.lines" dataKey="invoiceLineId" styleClass="p-datatable-gridlines dense-table line-table" [tableStyle]="{ 'min-width': '105rem' }">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Shipment</th>
                                    <th>Delivery</th>
                                    <th>Charge</th>
                                    <th>Description</th>
                                    <th class="text-right">Qty</th>
                                    <th class="text-right">Sales</th>
                                    <th class="text-right">VAT</th>
                                    <th class="text-right">Gross Total</th>
                                    <th class="text-right">Cost of Sales</th>
                                    <th class="text-right">Item Profit</th>
                                    <th class="text-right">Gross Profit</th>
                                    <th class="text-right">GP %</th>
                                    <th>Cost Source</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-line>
                                <tr>
                                    <td>{{ line.headerReferenceNumber || '-' }}</td>
                                    <td>{{ line.detailReferenceNumber || '-' }}</td>
                                    <td>{{ line.itemNumber || '-' }}</td>
                                    <td>{{ line.itemDescription || '-' }}</td>
                                    <td class="text-right">{{ line.quantity | number:'1.0-2' }}</td>
                                    <td class="text-right">{{ line.salesAmount | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                                    <td class="text-right">{{ line.vatAmount | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                                    <td class="text-right">{{ line.grossTotal | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                                    <td class="text-right">{{ line.costOfSales | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                                    <td class="text-right">{{ line.itemProfit | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}</td>
                                    <td class="text-right" [ngClass]="{'text-green-600': line.grossProfit >= 0, 'text-red-600': line.grossProfit < 0}">
                                        {{ line.grossProfit | currency: row.currencyCode || currencyCode() : 'symbol' : '1.2-2' }}
                                    </td>
                                    <td class="text-right">{{ line.grossProfitPercent | number:'1.2-2' }}%</td>
                                    <td><p-tag [value]="line.costSource || 'UNMATCHED'" [severity]="costSeverity(line.costSource)"></p-tag></td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr>
                                    <td colspan="13" class="text-center">No invoice lines available.</td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="footer">
                <tr class="grand-total-row">
                    <td colspan="4" class="text-right">Grand Total</td>
                    <td class="text-right">{{ summary.grossTotal | currency: currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ summary.salesAmount | currency: currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ summary.vatAmount | currency: currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ summary.costOfSales | currency: currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ summary.itemProfit | currency: currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ summary.grossProfit | currency: currencyCode() : 'symbol' : '1.2-2' }}</td>
                    <td class="text-right">{{ summary.grossProfitPercent | number:'1.2-2' }}%</td>
                    <td colspan="2"></td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="13" class="text-center">No posted sales vouchers found.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>
